/**
 * Articles reducer and initial state for useArticles context.
 */

export const initialState = {
  articles: [],
  currentArticle: null,
  stats: null,
  pagination: {
    page: 1,
    per_page: 50,
    total: 0,
    pages: 0,
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isRegenerating: false,
  isRefetching: false,
  isLoadingStats: false,
  error: null,
  filters: {
    search: '',
    domain: '',
  },
};

export const articlesReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: null,
      };

    case 'SET_CREATING':
      return {
        ...state,
        isCreating: action.payload,
        error: null,
      };

    case 'SET_UPDATING':
      return {
        ...state,
        isUpdating: action.payload,
        error: null,
      };

    case 'SET_DELETING':
      return {
        ...state,
        isDeleting: action.payload,
        error: null,
      };

    case 'SET_REGENERATING':
      return {
        ...state,
        isRegenerating: action.payload,
        error: null,
      };

    case 'SET_REFETCHING':
      return {
        ...state,
        isRefetching: action.payload,
        error: null,
      };

    case 'FETCH_ARTICLES_SUCCESS':
      return {
        ...state,
        isLoading: false,
        articles: action.payload.append
          ? [...state.articles, ...action.payload.articles]
          : action.payload.articles,
        pagination: action.payload.pagination,
        error: null,
      };

    case 'FETCH_ARTICLE_SUCCESS':
      return {
        ...state,
        isLoading: false,
        currentArticle: action.payload,
        error: null,
      };

    case 'CREATE_ARTICLE_SUCCESS':
      return {
        ...state,
        isCreating: false,
        articles: [action.payload, ...state.articles],
        stats: state.stats ? { ...state.stats, total_articles: state.stats.total_articles + 1 } : null,
        error: null,
      };

    case 'UPDATE_ARTICLE_SUCCESS': {
      const updatedArticles = state.articles.map(article =>
        article.id === action.payload.id ? action.payload : article
      );

      // Sort articles: unread first (is_read: false/null), then read (is_read: true)
      // Within each group, maintain chronological order (newest first)
      const sortedArticles = updatedArticles.sort((a, b) => {
        // Primary sort: read status (unread first)
        if (a.is_read !== b.is_read) {
          if (a.is_read && !b.is_read) return 1;  // read article goes after unread
          if (!a.is_read && b.is_read) return -1; // unread article goes before read
        }
        // Secondary sort: creation date (newest first within each group)
        return new Date(b.created_at) - new Date(a.created_at);
      });

      return {
        ...state,
        isUpdating: false,
        isRegenerating: false,
        isRefetching: false,
        articles: sortedArticles,
        currentArticle: state.currentArticle?.id === action.payload.id
          ? action.payload
          : state.currentArticle,
        error: null,
      };
    }

    case 'DELETE_ARTICLE_SUCCESS':
      return {
        ...state,
        isDeleting: false,
        articles: state.articles.filter(article => article.id !== action.payload),
        currentArticle: state.currentArticle?.id === action.payload ? null : state.currentArticle,
        stats: state.stats ? { ...state.stats, total_articles: state.stats.total_articles - 1 } : null,
        error: null,
      };

    case 'SET_LOADING_STATS':
      return {
        ...state,
        isLoadingStats: action.payload,
        error: null,
      };

    case 'FETCH_STATS_SUCCESS':
      return {
        ...state,
        stats: action.payload,
        isLoadingStats: false,
        error: null,
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'SET_PER_PAGE':
      return {
        ...state,
        pagination: { ...state.pagination, per_page: action.payload, page: 1 },
      };

    case 'SET_ERROR':
      return {
        ...state,
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isLoadingStats: false,
        error: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'CLEAR_CURRENT_ARTICLE':
      return {
        ...state,
        currentArticle: null,
      };

    case 'CLEAR_OPERATION_STATES':
      return {
        ...state,
        isUpdating: false,
        isRegenerating: false,
        isRefetching: false,
      };

    default:
      return state;
  }
};
